import LikeButton from "components/likes/LikeButton";
import LocalizedDate from "components/shared/LocalizedDate";
import PrivacyIcon from "components/shared/PrivacyIcon";
import UserByline from "components/users/UserByline";
import Badge from "hardcover-ui/components/Badge";
import Icon from "hardcover-ui/components/Icon";
import Text from "hardcover-ui/components/Text";
import TextLink from "hardcover-ui/components/TextLink";
import faLink from "icons/solid/link.svg";
import faExclamationTriangle from "icons/solid/triangle-exclamation.svg";
import LikeableType from "types/LikeableType";
import {
  BookSerializersBookByline,
  UserBookSerializersUserBookReview,
} from "types/serializers";

import BookReviewLink from "./BookReviewLink";
import BookReviewText from "./BookReviewText";
import EditReviewButton from "./EditReviewButton";
import FlagReviewButton from "./FlagReviewButton";
import Stars from "./Stars";

export interface BookReviewProps {
  userBook: UserBookSerializersUserBookReview;
  book: BookSerializersBookByline;
  linkToReview?: boolean;
  className?: string;
  expanded?: boolean;
  expandClass?: string;
  showUser?: boolean;
  allowCollapse?: boolean;
}

const BookReview = ({
  book,
  userBook,
  linkToReview = true,
  className = "",
  expanded = false,
  showUser = true,
  allowCollapse = true,
  expandClass = "max-h-64",
}: BookReviewProps) => {
  return (
    <div className={`space-y-1 max-w-screen ${className}`}>
      <div className="text-gray-400 text-sm flex flex-col md:flex-row flex-wrap gap-2 justify-between space-x-1">
        {showUser && <UserByline user={userBook.user} size="md" match />}
        <Stars rating={userBook.rating} size={6} />
      </div>

      {userBook.reviewHasSpoilers && (
        <p>
          <span className="bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-lg">
            <Icon
              icon={faExclamationTriangle}
              size="sm"
              className="fill-yellow-300 mr-1"
            />
            Contains spoilers
          </span>
        </p>
      )}
      {userBook.hasReview && (
        <BookReviewText
          reviewSlate={userBook.reviewSlate}
          expandClass={expandClass}
          expanded={expanded || userBook.reviewLength < 300}
          allowCollapse={allowCollapse}
        />
      )}

      <BookReviewLink userBook={userBook} />

      <div className="flex flex-row justify-between pt-4">
        <div className="flex flex-row space-x-4">
          <LikeButton likeable={userBook as LikeableType} showLikes={false} />
          {userBook.sponsoredReview && (
            <Badge variant="accent">Sponsored</Badge>
          )}
        </div>
        <div className="flex flex-row space-x-2 items-center">
          {userBook.privacySettingId !== 1 && (
            <PrivacyIcon
              privacySettingId={userBook.privacySettingId}
              size="md"
              className=""
            />
          )}
          {linkToReview && userBook.privacySettingId === 1 ? (
            <TextLink
              href={`/books/${book.slug}/reviews/@${userBook.user.username}`}
              variant="secondary"
              underline={true}
              size="sm"
              className="font-semibold space-x-1"
            >
              <Icon icon={faLink} size="md" />
              <span>
                {userBook.reviewedAt ? (
                  <LocalizedDate
                    date={String(userBook.reviewedAt)}
                    format="long"
                  />
                ) : (
                  "View"
                )}
              </span>
            </TextLink>
          ) : (
            <Text variant="secondary" className="font-semibold" size="sm">
              {userBook.reviewedAt ? (
                <LocalizedDate
                  date={String(userBook.reviewedAt)}
                  format="long"
                />
              ) : (
                ""
              )}
            </Text>
          )}
          <EditReviewButton userId={userBook.userId} slug={book.slug} />
          <FlagReviewButton userBookId={userBook.id} />
        </div>
      </div>
    </div>
  );
};

export default BookReview;
